import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Calendar as CalendarIcon, Info, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { AdSlot } from '../components/ads/AdSlot';
interface MoonCalendarPageProps {
  onNavigate: (page: string) => void;
}
export function MoonCalendarPage({
  onNavigate
}: MoonCalendarPageProps) {
  const currentDate = new Date().toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-25739">
      {/* Header */}
      <div className="bg-slate-900 text-white pt-6 pb-16 relative overflow-hidden" data-id="element-25740">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-25741" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-25742" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-25743">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Лунный календарь'
        }]} className="mb-8 text-slate-300" data-id="element-25744" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-25745">
            <div className="max-w-2xl" data-id="element-25746">
              <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-25747">
                Обновляется ежедневно
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-id="element-25748">
                Лунный календарь
              </h1>
              <p className="text-xl text-slate-300 mb-2 capitalize" data-id="element-25749">
                {currentDate}
              </p>
              <p className="text-slate-400" data-id="element-25750">
                Следите за фазами Луны, чтобы планировать важные дела, стрижки,
                посадки и понимать общий энергетический фон дня.
              </p>
            </div>

            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 120,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-48 h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-400 shadow-[0_0_50px_rgba(255,255,255,0.2)] flex items-center justify-center relative overflow-hidden" data-id="element-25751">
              {/* Simple moon phase representation (Waxing Gibbous) */}
              <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-80 mix-blend-multiply" data-id="element-25752" />
              <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-25753" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8 relative z-20" data-id="element-25754">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-25755">
          {/* Left Column: Current Status */}
          <div className="lg:col-span-2 space-y-8" data-id="element-25756">
            <Card className="p-6 sm:p-8 shadow-xl border-0" data-id="element-25757">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-25758">
                Энергия сегодняшнего дня
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-25759">
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25760">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25761">
                    Фаза Луны
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25762">
                    Растущая Луна <span className="text-indigo-500" data-id="element-25763">🌔</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25764">
                    Освещенность: 78%
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25765">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25766">
                    Лунный день
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25767">
                    11-й лунный день
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25768">
                    Символ: Огненный меч
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25769">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25770">
                    Луна в знаке
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25771">
                    Лев <span className="text-amber-500" data-id="element-25772">♌</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25773">
                    Стихия: Огонь
                  </div>
                </div>

                <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100" data-id="element-25774">
                  <div className="text-sm text-rose-600 font-medium mb-1 flex items-center gap-1" data-id="element-25775">
                    <AlertCircle className="w-4 h-4" data-id="element-25776" /> Внимание
                  </div>
                  <div className="text-xl font-bold text-slate-900" data-id="element-25777">
                    Луна без курса
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25778">
                    С 14:30 до 17:45 (МСК)
                  </div>
                </div>
              </div>

              <div className="prose prose-slate max-w-none" data-id="element-25779">
                <p data-id="element-25780">
                  Один из самых энергетически мощных дней лунного месяца.
                  Энергия бьет ключом, пробуждаются скрытые резервы организма.
                  Отличный день для активных действий, начала новых проектов и
                  физических нагрузок.
                </p>
                <p data-id="element-25781">
                  Луна во Льве добавляет желания быть в центре внимания, творить
                  и радовать себя. Однако в период "Луны без курса" (с 14:30 до
                  17:45) лучше воздержаться от принятия важных решений и
                  подписания документов — дела, начатые в это время, могут пойти
                  не по плану.
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-25782">
              <Card className="p-6 border-emerald-200 bg-emerald-50/30" data-id="element-25783">
                <div className="flex items-center gap-2 mb-4" data-id="element-25784">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-25785" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-25786">
                    Благоприятно
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-25787">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25788">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25789">•</span>{' '}
                    Творческие начинания
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25790">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25791">•</span>{' '}
                    Физическая активность и спорт
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25792">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25793">•</span> Стрижка
                    волос (для быстрого роста)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25794">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25795">•</span> Покупки
                    одежды и украшений
                  </li>
                </ul>
              </Card>

              <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-25796">
                <div className="flex items-center gap-2 mb-4" data-id="element-25797">
                  <XCircle className="w-6 h-6 text-rose-500" data-id="element-25798" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-25799">
                    Не рекомендуется
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-25800">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25801">
                    <span className="text-rose-500 mt-0.5" data-id="element-25802">•</span> Пассивность
                    и лень
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25803">
                    <span className="text-rose-500 mt-0.5" data-id="element-25804">•</span>{' '}
                    Хирургические операции на сердце
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25805">
                    <span className="text-rose-500 mt-0.5" data-id="element-25806">•</span> Важные дела
                    в период "Луны без курса"
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25807">
                    <span className="text-rose-500 mt-0.5" data-id="element-25808">•</span> Конфликты с
                    начальством
                  </li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Right Column: Mini Calendar & Info */}
          <div className="space-y-6" data-id="element-25809">
            <Card className="p-6" data-id="element-25810">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-25811">
                <CalendarIcon className="w-5 h-5 text-indigo-500" data-id="element-25812" />
                Ближайшие фазы
              </h3>
              <div className="space-y-4" data-id="element-25813">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25814">
                  <div className="flex items-center gap-3" data-id="element-25815">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-25816">
                      🌕
                    </div>
                    <div data-id="element-25817">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25818">
                        Полнолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25819">в Деве</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25820">
                    24 фев
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25821">
                  <div className="flex items-center gap-3" data-id="element-25822">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-25823">
                      🌗
                    </div>
                    <div data-id="element-25824">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25825">
                        Третья четверть
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25826">в Стрельце</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25827">
                    3 мар
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25828">
                  <div className="flex items-center gap-3" data-id="element-25829">
                    <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-lg" data-id="element-25830">
                      🌑
                    </div>
                    <div data-id="element-25831">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25832">
                        Новолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25833">в Рыбах</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25834">
                    10 мар
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100" data-id="element-25835">
              <div className="flex items-start gap-3" data-id="element-25836">
                <Info className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-25837" />
                <div data-id="element-25838">
                  <h4 className="font-bold text-indigo-900 mb-2" data-id="element-25839">
                    Что такое Луна без курса?
                  </h4>
                  <p className="text-sm text-indigo-800/80 leading-relaxed" data-id="element-25840">
                    Это период, когда Луна сделала свой последний мажорный
                    аспект в одном знаке зодиака, но еще не перешла в следующий.
                    В это время дела часто идут впустую, планы срываются, а
                    начатые проекты не приносят ожидаемого результата. Лучше
                    посвятить это время рутине и отдыху.
                  </p>
                </div>
              </div>
            </div>

            {/* Cross-links */}
            <div className="space-y-3" data-id="element-25841">
              <button onClick={() => onNavigate('daily-horoscope')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-25842">
                <span className="text-xl" data-id="element-25843">☀️</span>
                <div data-id="element-25844">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-25845">
                    Гороскоп на сегодня
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25846">
                    Прогноз для вашего знака
                  </div>
                </div>
              </button>
              <button onClick={() => onNavigate('calendar')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-25847">
                <span className="text-xl" data-id="element-25848">📅</span>
                <div data-id="element-25849">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-25850">
                    Астро-календарь
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25851">
                    Затмения и ретрограды
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-25852">
          <AdSlot slotId="SLOT-11" onNavigate={onNavigate} data-id="element-25853" />
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-25854">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-25855" />
          <div className="relative z-10" data-id="element-25856">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-25857">
              Узнайте, как Луна влияет именно на вас
            </h2>
            <p className="text-indigo-200 mb-8 max-w-xl mx-auto" data-id="element-25858">
              Постройте свою натальную карту и получите персональный анализ
              лунных транзитов.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-25859">
              <button onClick={() => onNavigate('service-natal')} className="px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors" data-id="element-25860">
                Заказать натальную карту
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors" data-id="element-25861">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}